import { RequestDebtRegistryConsent } from './RequestDebtRegistryConsent';
import { DebtRegistryTable } from './DebtRegistryTable';
import { debt_register_hooks, usePerson } from '@axo/shared/data-access/hooks';
import styles from './DebtRegistryView.module.css';
import { Button, NonIdealState, Spinner } from '@blueprintjs/core';
import { AppToaster } from '../AppToaster';

export function DebtRegistryView({
  applicationID,
  customerID,
  personID,
}: {
  applicationID: string;
  customerID: string;
  personID: string;
}) {
  const { data: personData, isLoading: isLoadingPersonData } =
    usePerson(personID);
  const { mutateAsync: updateDebtRegistry, isLoading: isUpdatingDebtRegistry } =
    debt_register_hooks.useUpdateDebtRegistryData();
  const {
    data: debtRegisterConsentData,
    isLoading: isLoadingRegisterConsentData,
  } = debt_register_hooks.useCachedDebtRegistryConsent({
    applicationID,
    personID,
  });

  // Gated on consent: a 404 auto-triggers /update, which must not run unconsented.
  const { data: debtRegistryDebts, isLoading: isLoadingDebtRegistryDebts } =
    debt_register_hooks.useDebtRegistryDebts(
      { applicationID, SSN: personData?.SocialSecurityNumber },
      { enabled: Boolean(debtRegisterConsentData) }
    );

  if (
    isLoadingRegisterConsentData ||
    isLoadingPersonData ||
    isLoadingDebtRegistryDebts
  ) {
    return (
      <NonIdealState
        className={styles.padding}
        icon={<Spinner />}
        title="Loading data from debt registry."
      />
    );
  }

  if (!personData) return null;

  if (!debtRegisterConsentData) {
    return (
      <RequestDebtRegistryConsent
        applicationID={applicationID}
        customerID={customerID}
        SSN={personData.SocialSecurityNumber}
      />
    );
  }

  return (
    <>
      <div className={styles.actionButtons}>
        <Button
          onClick={() => {
            updateDebtRegistry(
              { applicationID, SSN: personData.SocialSecurityNumber },
              {
                onSuccess: () => {
                  AppToaster.then((toaster) =>
                    toaster.show({
                      message: 'Debt registry updated',
                      intent: 'success',
                    })
                  );
                },
              }
            );
          }}
          icon="refresh"
          loading={isUpdatingDebtRegistry}
        />
      </div>
      {debtRegistryDebts ? (
        <DebtRegistryTable
          debts={debtRegistryDebts.debts}
          applicationID={applicationID}
          personID={personID}
        />
      ) : (
        <NonIdealState
          className={styles.padding}
          icon="search"
          title="No debts found for this applicant"
        />
      )}
    </>
  );
}
